import { useTranslation } from "react-i18next";
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Button, Skeleton } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { globalStore } from "../../../../../stores/stores";
import AddChapter from "../../../../drawer/add-chapter/AddChapter";
import "../TreeCourseConstruct.scss";
import { ChapterContainer } from "./ChapterContainer";
import DragItem from "./DragItem";
import { Chapter } from "./types";

interface Props {
  initialData: any[];
  dataConfig: any,
  onOrderChange: (updatedOrder: any[]) => void;

}

function MultipleContainers({ initialData, onOrderChange, dataConfig }: Props) {
  const { t } = useTranslation();
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [activeItem, setActiveItem] = useState<any>(null);
  const [activeItemType, setActiveItemType] = useState<"CHAPTER" | "LESSON" | "RESOURCE" | null>(null);
  const { reload, setReload } = globalStore()
  const [isOpenDrawer, setIsOpenDrawer] = useState(false);
  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));
  const [isLoading, setIsLoading] = useState<boolean>(true)

  const { id } = useParams()

  const findItemById = (id: number | string) => {
    for (let c = 0; c < chapters.length; c++) {
      if (chapters[c].id === id)
        return { type: "CHAPTER" as const, chapterIndex: c };
      for (let l = 0; l < chapters[c].children.length; l++) {
        if (chapters[c].children[l].id === id)
          return { type: "LESSON" as const, chapterIndex: c, lessonIndex: l };
        for (let r = 0; r < chapters[c].children[l].children.length; r++) {
          if (chapters[c].children[l].children[r].id === id)
            return {
              type: "RESOURCE" as const,
              chapterIndex: c,
              lessonIndex: l,
              resourceIndex: r,
            };
        }
      }
    }
    return null;
  };

  const getItemById = (id: number) => {
    const info = findItemById(id);
    if (!info) return null;
    switch (info.type) {
      case "CHAPTER":
        return chapters[info.chapterIndex];
      case "LESSON":
        return chapters[info.chapterIndex].children[info.lessonIndex];
      case "RESOURCE":
        return chapters[info.chapterIndex].children[info.lessonIndex].children[info.resourceIndex];
      default:
        return null;
    }
  };

  const handleDragStart = (event: DragEndEvent) => {
    const activeId = event.active.id as number;
    const info = findItemById(activeId);

    setActiveId(activeId);
    setActiveItem(getItemById(activeId));
    setActiveItemType(info?.type ?? null);
  };

  function convertDataForApi(chapters: Chapter[]) {
    return chapters.map((chapter, chapterIndex) => ({
      id: chapter.id,
      orderNumber: chapterIndex + 1,
      sequences: chapter.children?.map((lesson, lessonIndex) => ({
        id: lesson.id,
        orderNumber: lessonIndex + 1,
        units: lesson.children?.map((resource, resourceIndex) => ({
          id: resource.id,
          orderNumber: resourceIndex + 1,
        })) ?? [],
      })) ?? [],
    }));
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    setActiveItem(null);
    setActiveItemType(null);

    if (!over || active.id === over.id) return;

    const from = findItemById(active.id);
    const to = findItemById(over.id);

    if (!from || !to) return;

    setChapters((prev) => {
      const copy = [...prev];

      if (from.type === "CHAPTER" && to.type === "CHAPTER") {
        const updatedChapters = arrayMove(copy, from.chapterIndex, to.chapterIndex);
        onOrderChange(convertDataForApi(updatedChapters));
        return updatedChapters;
      }

      if (from.type === "LESSON") {
        const [movedLesson] = copy[from.chapterIndex].children.splice(
          from.lessonIndex,
          1
        );
        copy[to.chapterIndex].children.splice(to.lessonIndex ?? 0, 0, movedLesson);
      }

      if (from.type === "RESOURCE") {
        if (
          typeof from.chapterIndex === "undefined" ||
          typeof from.lessonIndex === "undefined" ||
          typeof from.resourceIndex === "undefined" ||
          typeof to.chapterIndex === "undefined" ||
          typeof to.lessonIndex === "undefined"
        ) {
          return copy;
        }

        const [movedResource] = copy[from.chapterIndex]
          .children[from.lessonIndex]
          .children.splice(from.resourceIndex, 1);

        const toResourceIndex =
          to.resourceIndex ??
          copy[to.chapterIndex].children[to.lessonIndex].children.length;

        copy[to.chapterIndex].children[to.lessonIndex].children.splice(
          toResourceIndex,
          0,
          movedResource
        );
      }

      onOrderChange(convertDataForApi(copy));

      return copy;
    });
  };

  useEffect(() => {
    setChapters(Array.isArray(initialData) ? initialData : []);
  }, [initialData]);

  const renderedChapters = useMemo(() => {

    return chapters.map((chapter) => (
      <ChapterContainer key={chapter.id} chapter={chapter} dataConfig={dataConfig} />
    ));
  }, [chapters, dataConfig]);


  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  return (

    <div className="tree-course-construct w-full">
      {isLoading ? <>
        {[1, 2, 3].map((_, i) => (
          <Skeleton key={i} active paragraph={{ rows: 3 }} style={{ marginBottom: 24 }} />
        ))}
      </> : <>
        <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          <SortableContext
            items={chapters.map((c) => c.id)}
            strategy={verticalListSortingStrategy}
          >
            {renderedChapters}
          </SortableContext>

          <DragOverlay>
            {activeId && activeItem && activeItemType && (
              <DragItem
                item={activeItem}
                type={activeItemType}
                dataConfig={dataConfig}
              />
            )}
          </DragOverlay>
        </DndContext>

        <Button
          onClick={() => setIsOpenDrawer(true)}
          className="button-add-chapter"
        >
          <div className="add-button">
            <div>+</div>
            {t("Thêm chương", "Thêm chương")}
          </div>
        </Button>
      </>}
      {isOpenDrawer &&
        <AddChapter
          id={Number(id)}
          title="Basic Drawer"
          setIsOpenDrawer={setIsOpenDrawer}
          open={isOpenDrawer}
          handleDetail={() => setReload(!reload)}
        />}
    </div>

  );
}
export default React.memo(MultipleContainers);